import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  Alert,
  Paper,
  alpha,
  Tooltip,
  Avatar,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Air as BreathingIcon,
  Psychology as MindfulnessIcon,
  DirectionsRun as MovementIcon,
  Favorite as GratitudeIcon,
  Psychology as ReflectionIcon,
  School as BeginnerIcon,
  TrendingUp as IntermediateIcon,
  Star as AdvancedIcon,
} from "@mui/icons-material";
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import { useMicroPractices } from "../../hooks/useRedux";
import { MicroPractice } from "../../store/slices/types";
import { toast } from "react-toastify";

const MicroPractices: React.FC = () => {
  const navigate = useNavigate();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  
  const {
    microPractices,
    loading,
    error,
    deleteMicroPractice,
    pagination,
  } = useMicroPractices(
    paginationModel.page + 1,
    paginationModel.pageSize
  );

  // Filter micro practices based on active filter
  const filteredMicroPractices = activeFilter
    ? microPractices.filter((practice) => practice.category === activeFilter)
    : microPractices;

  // Calculate statistics
  const totalMicroPractices = microPractices.length;
  const breatheResetPractices = microPractices.filter(
    (p) => p.category === "Breathe & Reset"
  ).length;
  const reframeFocusPractices = microPractices.filter(
    (p) => p.category === "Reframe & Focus"
  ).length;
  const gratitudeMindfulnessPractices = microPractices.filter(
    (p) => p.category === "Gratitude & Mindfulness"
  ).length;
  const reflectRecoverPractices = microPractices.filter(
    (p) => p.category === "Reflect & Recover"
  ).length;
  const movementPractices = microPractices.filter(
    (p) => p.category === "Movement"
  ).length;

  const handleFilterClick = (category: string | null) => {
    setActiveFilter(category);
  };

  if (error) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "Breathe & Reset":
        return <BreathingIcon />;
      case "Reframe & Focus":
        return <MindfulnessIcon />;
      case "Gratitude & Mindfulness":
        return <GratitudeIcon />;
      case "Reflect & Recover":
        return <ReflectionIcon />;
      case "Movement":
        return <MovementIcon />;
      default:
        return <MindfulnessIcon />;
    }
  };

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case "beginner":
        return <BeginnerIcon />;
      case "intermediate":
        return <IntermediateIcon />;
      case "advanced":
        return <AdvancedIcon />;
      default:
        return <BeginnerIcon />;
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "beginner":
        return "success";
      case "intermediate":
        return "warning";
      case "advanced":
        return "error";
      default:
        return "default";
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "Breathe & Reset":
        return "#4facfe";
      case "Reframe & Focus":
        return "#667eea";
      case "Gratitude & Mindfulness":
        return "#fa709a";
      case "Reflect & Recover":
        return "#f093fb";
      case "Movement":
        return "#43e97b";
      default:
        return "#667eea";
    }
  };

  const formatDate = (date: number | string | Date | undefined) => {
    if (!date) return "N/A";
    const dateObj = typeof date === 'number' ? new Date(date) : new Date(date);
    if (isNaN(dateObj.getTime())) return "N/A";
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns: GridColDef[] = [
    {
      field: "category",
      headerName: "",
      flex: 0.1,
      minWidth: 60,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: getCategoryColor(params.value),
          }}
        >
          {getCategoryIcon(params.value)}
        </Avatar>
      ),
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1.5,
      minWidth: 200,
      filterable: true,
      renderCell: (params) => (
        <Box>
          <Typography variant='body2' fontWeight='600'>
            {params.value}
          </Typography>
          <Typography variant='caption' color='textSecondary' noWrap>
            {params.row.description?.substring(0, 50)}...
          </Typography>
        </Box>
      ),
    },
    {
      field: "duration",
      headerName: "Duration",
      flex: 0.4,
      minWidth: 80,
      renderCell: (params) => (
        <Chip
          label={`${params.value} min`}
          size='small'
          color='primary'
          variant='outlined'
        />
      ),
    },
    {
      field: "difficulty",
      headerName: "Difficulty",
      flex: 0.6,
      minWidth: 100,
      filterable: true,
      renderCell: (params) => (
        <Chip
          icon={getDifficultyIcon(params.value)}
          label={params.value}
          size='small'
          color={getDifficultyColor(params.value) as any}
          variant='filled'
        />
      ),
    },
    {
      field: "category",
      headerName: "Category",
      flex: 0.6,
      minWidth: 100,
      filterable: true,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size='small'
          sx={{
            backgroundColor: getCategoryColor(params.value),
            color: "white",
            "&:hover": {
              backgroundColor: getCategoryColor(params.value),
              opacity: 0.8,
            },
          }}
        />
      ),
    },
    {
      field: "instructions",
      headerName: "Instructions",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant='body2' fontWeight='600'>
          {params.value?.length || 0} steps
        </Typography>
      ),
    },
    {
      field: "benefits",
      headerName: "Benefits",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant='body2' fontWeight='600'>
          {params.value?.length || 0} benefits
        </Typography>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 0.8,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => (
        <Typography variant='body2'>{formatDate(params.value)}</Typography>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Last Updated",
      flex: 0.8,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => (
        <Typography variant='body2'>{formatDate(params.value)}</Typography>
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 0.3,
      minWidth: 80,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Tooltip title='Edit Micro Practice'>
              <EditIcon />
            </Tooltip>
          }
          label='Edit'
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title='Delete Micro Practice'>
              <DeleteIcon />
            </Tooltip>
          }
          label='Delete'
          onClick={() => handleDelete(params.row.id)}
        />,
      ],
    },
  ];

  const handleAdd = () => {
    navigate("/micro-practices/add");
  };

  const handleEdit = (microPractice: MicroPractice) => {
    const id = microPractice._id || microPractice.id;
    if (id) {
      navigate(`/micro-practices/edit/${id}`);
    }
  };

  const handleDelete = async (id: string) => {
    const microPractice = microPractices.find((mp) => mp.id === id);
    const microPracticeTitle = microPractice?.title || "Unknown Micro Practice";

    if (
      window.confirm(
        `Are you sure you want to delete "${microPracticeTitle}"? This action cannot be undone.`
      )
    ) {
      try {
        await deleteMicroPractice(id);
        toast.success("micro practice is deleted");
      } catch (error) {
        console.error("Failed to delete micro practice:", error);
        toast.error(
          `❌ Failed to delete micro practice: ${
            error instanceof Error ? error.message : "Unknown error"
          }`
        );
      }
    }
  };


  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant='h4' fontWeight='700' gutterBottom>
              Micro Practices Management
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              Create and manage quick wellness activities
            </Typography>
          </Box>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
              },
            }}
          >
            Create Micro Practice
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={4} md={2}>
            <Card
              sx={{
                background:
                  activeFilter === null
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                borderRadius: 1.5,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform: activeFilter === null ? "scale(1.02)" : "scale(1)",
                boxShadow:
                  activeFilter === null
                    ? "0 8px 25px rgba(102, 126, 234, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(102, 126, 234, 0.3)",
                },
              }}
              onClick={() => handleFilterClick(null)}
            >
              <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h5'
                      fontWeight='bold'
                      sx={{ fontSize: "1.25rem" }}
                    >
                      {totalMicroPractices}
                    </Typography>
                    <Typography
                      variant='caption'
                      sx={{ opacity: 0.9, fontSize: "0.7rem" }}
                    >
                      Total Practices
                    </Typography>
                  </Box>
                  <MindfulnessIcon sx={{ fontSize: 28, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card
              sx={{
                borderRadius: 1.5,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform:
                  activeFilter === "Breathe & Reset"
                    ? "scale(1.02)"
                    : "scale(1)",
                boxShadow:
                  activeFilter === "Breathe & Reset"
                    ? "0 8px 25px rgba(79, 172, 254, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                border:
                  activeFilter === "Breathe & Reset"
                    ? "2px solid #4facfe"
                    : "2px solid transparent",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(79, 172, 254, 0.3)",
                },
              }}
              onClick={() => handleFilterClick("Breathe & Reset")}
            >
              <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h5'
                      fontWeight='bold'
                      color='info.main'
                      sx={{ fontSize: "1.25rem" }}
                    >
                      {breatheResetPractices}
                    </Typography>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ fontSize: "0.7rem" }}
                    >
                      Breathe & Reset
                    </Typography>
                  </Box>
                  <BreathingIcon sx={{ fontSize: 28, color: "info.main" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card
              sx={{
                borderRadius: 1.5,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform:
                  activeFilter === "Reframe & Focus"
                    ? "scale(1.02)"
                    : "scale(1)",
                boxShadow:
                  activeFilter === "Reframe & Focus"
                    ? "0 8px 25px rgba(102, 126, 234, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                border:
                  activeFilter === "Reframe & Focus"
                    ? "2px solid #667eea"
                    : "2px solid transparent",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(102, 126, 234, 0.3)",
                },
              }}
              onClick={() => handleFilterClick("Reframe & Focus")}
            >
              <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h5'
                      fontWeight='bold'
                      color='primary.main'
                      sx={{ fontSize: "1.25rem" }}
                    >
                      {reframeFocusPractices}
                    </Typography>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ fontSize: "0.7rem" }}
                    >
                      Reframe & Focus
                    </Typography>
                  </Box>
                  <MindfulnessIcon
                    sx={{ fontSize: 28, color: "primary.main" }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card
              sx={{
                borderRadius: 1.5,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform:
                  activeFilter === "Gratitude & Mindfulness"
                    ? "scale(1.02)"
                    : "scale(1)",
                boxShadow:
                  activeFilter === "Gratitude & Mindfulness"
                    ? "0 8px 25px rgba(250, 112, 154, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                border:
                  activeFilter === "Gratitude & Mindfulness"
                    ? "2px solid #fa709a"
                    : "2px solid transparent",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(250, 112, 154, 0.3)",
                },
              }}
              onClick={() => handleFilterClick("Gratitude & Mindfulness")}
            >
              <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h5'
                      fontWeight='bold'
                      sx={{ color: "#fa709a", fontSize: "1.25rem" }}
                    >
                      {gratitudeMindfulnessPractices}
                    </Typography>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ fontSize: "0.7rem" }}
                    >
                      Gratitude & Mindfulness
                    </Typography>
                  </Box>
                  <GratitudeIcon sx={{ fontSize: 28, color: "#fa709a" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card
              sx={{
                borderRadius: 1.5,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform:
                  activeFilter === "Reflect & Recover"
                    ? "scale(1.02)"
                    : "scale(1)",
                boxShadow:
                  activeFilter === "Reflect & Recover"
                    ? "0 8px 25px rgba(240, 147, 251, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                border:
                  activeFilter === "Reflect & Recover"
                    ? "2px solid #f093fb"
                    : "2px solid transparent",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(240, 147, 251, 0.3)",
                },
              }}
              onClick={() => handleFilterClick("Reflect & Recover")}
            >
              <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h5'
                      fontWeight='bold'
                      sx={{ color: "#f093fb", fontSize: "1.25rem" }}
                    >
                      {reflectRecoverPractices}
                    </Typography>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ fontSize: "0.7rem" }}
                    >
                      Reflect & Recover
                    </Typography>
                  </Box>
                  <ReflectionIcon sx={{ fontSize: 28, color: "#f093fb" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card
              sx={{
                borderRadius: 1.5,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform:
                  activeFilter === "Movement" ? "scale(1.02)" : "scale(1)",
                boxShadow:
                  activeFilter === "Movement"
                    ? "0 8px 25px rgba(67, 233, 123, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                border:
                  activeFilter === "Movement"
                    ? "2px solid #43e97b"
                    : "2px solid transparent",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(67, 233, 123, 0.3)",
                },
              }}
              onClick={() => handleFilterClick("Movement")}
            >
              <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h5'
                      fontWeight='bold'
                      color='success.main'
                      sx={{ fontSize: "1.25rem" }}
                    >
                      {movementPractices}
                    </Typography>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ fontSize: "0.7rem" }}
                    >
                      Movement
                    </Typography>
                  </Box>
                  <MovementIcon sx={{ fontSize: 28, color: "success.main" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Filter Indicator */}
      {activeFilter && (
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            Showing {activeFilter} practices:
          </Typography>
          <Chip
            label={activeFilter}
            color='primary'
            size='small'
            onDelete={() => handleFilterClick(null)}
          />
        </Box>
      )}

      {/* Data Table */}
      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <DataGrid
          rows={filteredMicroPractices}
          columns={columns}
          getRowId={(row) => row._id || row.id || String(Math.random())}
          paginationMode='server'
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={activeFilter ? filteredMicroPractices.length : (pagination?.total || 0)}
          loading={loading}
          autoHeight
          disableColumnMenu={false}
          disableColumnFilter={false}
          disableColumnSelector={false}
          filterModel={undefined}
          onFilterModelChange={() => {}}
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: alpha("#667eea", 0.1),
              borderBottom: `2px solid ${alpha("#667eea", 0.2)}`,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: alpha("#667eea", 0.05),
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
            },
            "& .MuiDataGrid-filterForm": {
              display: "flex",
              alignItems: "center",
              gap: 1,
            },
          }}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
            filter: {
              filterModel: {
                items: [],
              },
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default MicroPractices;
